import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { CircularProgress, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import TransferList from "components/TransferList/transfer_list.index";
import {
  useGetEstudiantesPorGrupoQuery,
  useGetEstudiantesQuery,
} from "services";
import formularioProf from "./schemas/form";

const EstudiantesForm = ({
  grupoById,
  idEst,
  handleSubmit,
  handleClose,
  isLoadingSubmit,
  oldValues,
  setOldValues,
}) => {
  const { formId } = formularioProf;
  const [offset, setOffset] = useState(0);
  const [busqueda, setBusqueda] = useState("");
  const [offsetBusqueda, setOffsetBusqueda] = useState(0);
  const [accumulatedData, setAccumulatedData] = useState([]);
  const [accumulatedDataBusqueda, setAccumulatedDataBusqueda] = useState([]);
  const [searchApi, setSearchApi] = useState("");

  const {
    data: estudiantes_data,
    isSuccess,
    isLoading,
  } = useGetEstudiantesQuery(
    {
      filter_params: {
        ordering:
          "user__apellido_paterno,user__apellido_materno,user__first_name,numero_de_control",
        asignado: "false",
        buscar: busqueda,
        estado: "Activo",
        limit: 50,
        offset: busqueda !== "" ? offsetBusqueda : offset,
        query:
          "{user{first_name,apellido_materno,apellido_paterno},numero_de_control,url,id}",
      },
      shape: {
        id: "id",
        url: "url",
        nombre:
          "user.apellido_paterno user.apellido_materno user.first_name numero_de_control",
      },
    },
    {
      refetchOnReconnect: true,
    },
  );

  const {
    data: estXGrupo,
    isLoading: isLoadingExG,
    isSuccess: isSuccessExG,
  } = useGetEstudiantesPorGrupoQuery(
    {
      filter_params: {
        distribucion_grupo__ciclo_interno__carrera:
          grupoById?.ciclo_interno?.carrera?.id,
        distribucion_grupo__grupo: grupoById?.grupo?.id,
        ordering:
          "estudiante__user__apellido_paterno,estudiante__user__apellido_materno,estudiante__user__first_name",
        query:
          "{estudiante{id,url,numero_de_control,user{apellido_paterno,apellido_materno,first_name}}}",
      },
      shape: {
        id: "estudiante.id",
        url: "estudiante.url",
        nombre:
          "estudiante.user.apellido_paterno estudiante.user.apellido_materno estudiante.user.first_name estudiante.numero_de_control",
      },
    },
    { refetchOnReconnect: true, skip: !grupoById },
  );

  const prevEstudiantesDataRef = useRef();

  useEffect(() => {
    if (estudiantes_data?.results && busqueda === "") {
      if (offset === 0) {
        setAccumulatedData(estudiantes_data.results);
      } else if (
        prevEstudiantesDataRef.current !== estudiantes_data.results &&
        busqueda === ""
      ) {
        setAccumulatedData((prevData) => {
          const isSubset = estudiantes_data.results.every((option) =>
            prevData.some((prevOption) => prevOption.id === option.id),
          );
          return isSubset
            ? prevData
            : [...prevData, ...estudiantes_data.results];
        });
      }
      prevEstudiantesDataRef.current = estudiantes_data.results;
    }
  }, [offset, estudiantes_data, busqueda]);

  useEffect(() => {
    if (estudiantes_data?.results && busqueda !== "") {
      if (offsetBusqueda === 0) {
        setAccumulatedDataBusqueda(estudiantes_data.results);
      } else if (
        prevEstudiantesDataRef.current !== estudiantes_data.results &&
        busqueda !== ""
      ) {
        setAccumulatedDataBusqueda((prevData) => [
          ...prevData,
          ...estudiantes_data.results,
        ]);
      }
      prevEstudiantesDataRef.current = estudiantes_data.results;
    }
  }, [offsetBusqueda, busqueda, estudiantes_data]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setBusqueda(searchApi);
      setOffsetBusqueda(0);
    }
  };

  const loadMore = () => {
    if (busqueda !== "") {
      setOffsetBusqueda((prev) => prev + 50);
    } else {
      setOffset((prev) => prev + 50);
    }
  };

  const isDataReady = isSuccess && (!isLoadingExG || idEst);

  return (
    <Formik
      initialValues={{
        estudiantes: oldValues?.estudiantes || [],
      }}
      enableReinitialize
      onSubmit={(values, actions) => {
        handleSubmit(values, actions);
      }}
    >
      {({ setFieldValue, values, isSubmitting }) => {
        // Inicializar los valores del lado derecho cuando estXGrupo cambia
        useEffect(() => {
          if (isSuccessExG && !idEst) {
            setFieldValue("estudiantes", estXGrupo);
            setOldValues((prev) => ({ ...prev, estudiantes: estXGrupo }));
          }
        }, [isSuccessExG, estXGrupo, setFieldValue, setOldValues, idEst]);

        return (
          <Form id={`${formId}-estudiantes`} autoComplete="off">
            {isLoading || isLoadingExG ? (
              <MDBox display="flex" justifyContent="center" p={3}>
                <CircularProgress color="inherit" />
              </MDBox>
            ) : null}

            {isDataReady && (
              <MDBox display="flex" flexDirection="column" p={1}>
                <MDBox width={{ xs: "100%", sm: "15rem" }} ml="auto" mb={2}>
                  <MDInput
                    placeholder="Buscar en la base de datos"
                    type="text"
                    value={searchApi}
                    size="small"
                    fullWidth
                    onChange={(e) => setSearchApi(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </MDBox>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TransferList
                      data={
                        busqueda !== ""
                          ? accumulatedDataBusqueda
                          : accumulatedData
                      }
                      generalTitle="Estudiantes"
                      setSubmittingData={(right) =>
                        setFieldValue("estudiantes", right)
                      }
                      rightData={values.estudiantes}
                      edit={!!idEst}
                      pagination={{
                        showLoadMoreButton: !!estudiantes_data?.next,
                        loadMore,
                      }}
                    />
                  </Grid>
                </Grid>

                <MDBox mt={4} display="flex" justifyContent="center" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={handleClose}
                    disabled={isSubmitting || isLoadingSubmit}
                  >
                    Cancelar
                  </MDButton>
                  <MDButton
                    type="submit"
                    variant="contained"
                    color="info"
                    disabled={isSubmitting || isLoadingSubmit}
                  >
                    Aceptar
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

EstudiantesForm.propTypes = {
  grupoById: PropTypes.object,
  idEst: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  isLoadingSubmit: PropTypes.bool,
  oldValues: PropTypes.object,
  setOldValues: PropTypes.func.isRequired,
};

export default EstudiantesForm;
